type RetentionConfig = {
  retentionMs: number;
  checkIntervalMs: number;
  activityThrottleMs: number;
  debug: boolean;
};

export const LAST_ACTIVITY_KEY = "biodata360:last-activity";

const safeParseNumber = (raw: unknown) => {
  if (typeof raw !== "string") return null;
  const trimmed = raw.trim();
  if (!trimmed) return null;
  const n = Number(trimmed);
  if (!Number.isFinite(n)) return null;
  return n;
};

export const getRetentionConfig = (): RetentionConfig => {
  const env = typeof import.meta !== "undefined" ? (import.meta as any).env : undefined;
  const hours = safeParseNumber(env?.VITE_DATA_RETENTION_HOURS);
  const ms = safeParseNumber(env?.VITE_DATA_RETENTION_MS);
  const checkIntervalMs = safeParseNumber(env?.VITE_DATA_RETENTION_CHECK_INTERVAL_MS);
  const activityThrottleMs = safeParseNumber(env?.VITE_DATA_RETENTION_ACTIVITY_THROTTLE_MS);
  const debug = String(env?.VITE_DATA_RETENTION_DEBUG || "").toLowerCase() === "true";

  const retentionMs =
    typeof ms === "number"
      ? ms
      : typeof hours === "number"
        ? hours * 60 * 60 * 1000
        : 12 * 60 * 60 * 1000;

  return {
    retentionMs,
    checkIntervalMs: typeof checkIntervalMs === "number" ? Math.max(10_000, checkIntervalMs) : 10 * 60 * 1000,
    activityThrottleMs: typeof activityThrottleMs === "number" ? Math.max(5000, activityThrottleMs) : 30_000,
    debug,
  };
};

export const getLastActivityMs = (): number | null => {
  if (typeof window === "undefined") return null;
  const storage = window.localStorage;
  if (!storage || typeof storage.getItem !== "function") return null;
  try {
    const raw = storage.getItem(LAST_ACTIVITY_KEY);
    const n = safeParseNumber(raw);
    return typeof n === "number" ? n : null;
  } catch {
    return null;
  }
};

export const setLastActivityMs = (value: number) => {
  if (typeof window === "undefined") return;
  const storage = window.localStorage;
  if (!storage || typeof storage.setItem !== "function") return;
  try {
    storage.setItem(LAST_ACTIVITY_KEY, String(value));
  } catch {
    void 0;
  }
};

export const touchLastActivity = (nowMs: number = Date.now()) => {
  setLastActivityMs(nowMs);
};

export const isExpiredByInactivity = (nowMs: number = Date.now(), config: RetentionConfig = getRetentionConfig()) => {
  const last = getLastActivityMs();
  if (typeof last !== "number") return false;
  return nowMs - last >= config.retentionMs;
};

const clearAllCookies = () => {
  if (typeof document === "undefined") return;
  const cookieString = document.cookie;
  if (!cookieString) return;
  const cookies = cookieString.split(";").map((c) => c.trim()).filter(Boolean);
  const hostParts = typeof location !== "undefined" ? location.hostname.split(".").filter(Boolean) : [];

  const domains: Array<string | undefined> = [undefined];
  if (hostParts.length >= 2) {
    domains.push(`.${hostParts.slice(-2).join(".")}`);
  }
  if (hostParts.length >= 3) {
    domains.push(`.${hostParts.slice(-3).join(".")}`);
  }

  const paths = ["/"];
  for (const cookie of cookies) {
    const eqIndex = cookie.indexOf("=");
    const name = eqIndex >= 0 ? cookie.slice(0, eqIndex) : cookie;
    for (const domain of domains) {
      for (const path of paths) {
        const domainPart = domain ? `; domain=${domain}` : "";
        document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=${path}${domainPart}`;
      }
    }
  }
};

const clearAllCacheStorage = async () => {
  if (typeof caches === "undefined") return;
  try {
    const keys = await caches.keys();
    await Promise.all(keys.map((k) => caches.delete(k)));
  } catch {
    void 0;
  }
};

const clearAllIndexedDb = async () => {
  if (typeof indexedDB === "undefined") return;
  try {
    const anyIndexedDb = indexedDB as any;
    if (typeof anyIndexedDb.databases === "function") {
      const dbs = (await anyIndexedDb.databases()) as Array<{ name?: string }>;
      await Promise.all(
        dbs
          .map((db) => db.name)
          .filter((name): name is string => typeof name === "string" && Boolean(name))
          .map(
            (name) =>
              new Promise<void>((resolve) => {
                const req = indexedDB.deleteDatabase(name);
                req.onsuccess = () => resolve();
                req.onerror = () => resolve();
                req.onblocked = () => resolve();
              })
          )
      );
    }
  } catch {
    void 0;
  }
};

export const purgeClientUserData = async (opts?: { debug?: boolean }) => {
  const debug = Boolean(opts?.debug);
  const log = (...args: any[]) => {
    if (!debug) return;
    try {
      console.info("[Retention]", ...args);
    } catch {
      void 0;
    }
  };

  log("Purging local/session storage");
  if (typeof window !== "undefined") {
    try {
      window.localStorage?.clear?.();
    } catch {
      void 0;
    }
    try {
      window.sessionStorage?.clear?.();
    } catch {
      void 0;
    }
  }

  log("Purging cookies");
  try {
    clearAllCookies();
  } catch {
    void 0;
  }

  log("Purging Cache Storage");
  await clearAllCacheStorage();

  log("Purging IndexedDB");
  await clearAllIndexedDb();
};

export const runRetentionCleanupIfNeeded = async (nowMs: number = Date.now()) => {
  const cfg = getRetentionConfig();
  const expired = isExpiredByInactivity(nowMs, cfg);
  if (!expired) {
    if (getLastActivityMs() === null) touchLastActivity(nowMs);
    return { purged: false };
  }

  await purgeClientUserData({ debug: cfg.debug });
  touchLastActivity(nowMs);
  return { purged: true };
};
